import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { GS1NotificationService } from '@gs1france/gs1-components-library';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, forkJoin, Observable, of, Subject, take, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { COUNTRIES } from '../../../config/countries';
import { AccessDataFromToken, DecodedAccessToken } from '../models/access-data-from-token.model';
import { InfoPerso } from '../models/info-perso.model';
import { Invoice } from '../models/invoice.model';
import { AccessData } from '../models/access-data.model';
import { AccessDataByEmailInputModel, AccountModel, PrivilegeModel } from '../models/access-data-by-email-input.model';

import { CompanyDetails, CompanyDisplayedSections } from '../models/compte-entreprise.model';
import { EcliSettings } from '../models/settings.model';
import { CompteEntrepriseService } from './compteEntreprise.service';
import { FormulaireDonneePersoService } from './formulaireDonneePerso.service';
import { InvoiceService } from './invoice.service';
import { ListeService } from './liste.service';
import { MandatService } from './mandat.service';
import { SettingService } from './settings.service';
import { AllSEPAMandates } from '@models/mandate.model';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private _platformId = inject(PLATFORM_ID);
  private _compteEntrepriseService = inject(CompteEntrepriseService);
  private _userService = inject(FormulaireDonneePersoService);
  private _mandate = inject(MandatService);
  private _listeService = inject(ListeService);
  private _invoiceService = inject(InvoiceService);
  private _notifService = inject(GS1NotificationService);
  private _settingsService = inject(SettingService);
  private _translate = inject(TranslateService);
  private _authService = inject(OAuthService);

  // Storage
  hasStorage: true;
  itemStorageList = ['accessData', 'multiple', 'tokenGs1', 'session', 'nomComplet'];

  // State flags
  isRedirected = false; // Is redirected to sign cgu or complete information

  // Access & Authentication
  accessDataValue: AccessData;

  // Multiple companies
  multiple: boolean;

  // User
  userValue: InfoPerso;

  contactFullName: string;
  contactEmail: string;

  // Company
  selectedCompany: CompanyDetails;

  packValue: string;
  pack: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  // Invoices & Mandates
  unpaidInvoicesValue: { count: number; toPay: Invoice[]; autoPay: Invoice[] };
  unpaidInvoices = new BehaviorSubject<{
    count: number;
    toPay: Invoice[];
    autoPay: Invoice[];
  }>(null);
  private loadingUnpaid = false;

  mandatValue: AllSEPAMandates;
  mandat = new BehaviorSubject<AllSEPAMandates>(null);
  private loadingMandat = false;

  // Settings
  settingsValue: EcliSettings;
  settings: Subject<EcliSettings> = new Subject<EcliSettings>();
  settingsLoading = false;

  // Search
  searchTextValue: string;
  searchText: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  public read(key: string): any {
    if (isPlatformBrowser(this._platformId)) {
      const str = localStorage.getItem(key);
      return str ? JSON.parse(str) : null;
    } else return null;
  }

  public readRaw(key: string): string | null {
    if (isPlatformBrowser(this._platformId)) {
      const str = localStorage.getItem(key);
      return str;
    } else {
      return null;
    }
  }

  public write(key: string, value: any): void {
    value = value === undefined ? null : value;
    localStorage.setItem(key, JSON.stringify(value));
  }

  public writeRaw(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  public resetStorage() {
    this.removeCompanyData();
    this.setPack(null);
    this.setAccountInfo(null);
    this.setUser(null);
  }

  public setAccessData(value: AccessData) {
    this.accessDataValue = value;
  }

  public removeAccessData() {
    this.accessDataValue = null;
  }

  public removeCompanyData() {
    this.removeAccessData();
    this.removeUnpaidInvoices();
    this.selectedCompany = null;
  }

  public updateUserWithName(firstName: string, lastName: string) {
    this.userValue.prenom = firstName;
    this.userValue.nom = lastName;
    this.setContactFullName(firstName, lastName);
  }

  public updateAccessDataWithName(firstName: string, lastName: string) {
    this.accessDataValue.contactFirstName = firstName;
    this.accessDataValue.contactLastName = lastName;
    this.setContactFullName(firstName, lastName);
  }

  public setSearchText(value: string) {
    this.searchTextValue = value;
    this.searchText.next(value);
    this.write('searchText', value);
  }

  public getSearchText(): string {
    if (!this.searchTextValue) {
      this.searchTextValue = this.read('searchText');
    }
    return this.searchTextValue;
  }

  public clearSearchText() {
    this.searchTextValue = null;
    this.searchText.next(null);
    localStorage.removeItem('searchText');
  }

  public setAccountInfo(accessDataFromToken: AccessDataFromToken) {
    if (accessDataFromToken) {
      this.setContactFullName(accessDataFromToken.contactFirstName, accessDataFromToken.contactLastName);
      this.contactEmail = accessDataFromToken.contactEmail;
      this.writeRaw('accountInfo', JSON.stringify(accessDataFromToken) ?? '');
    }
  }

  private setSettings(settings: EcliSettings) {
    this.settingsValue = settings;
    this.settings.next(this.settingsValue);
  }

  public getSettings(): Observable<EcliSettings> {
    if (this.settingsLoading) {
      return this.settings.asObservable();
    }
    if (!this.settingsValue) {
      this.settingsLoading = true;
      return this._settingsService.getSettings().pipe(
        tap((settings) => {
          this.setSettings(settings);
          this.settingsLoading = false;
        })
      );
    }
    return new BehaviorSubject(this.settingsValue).asObservable();
  }

  public setUser(user: InfoPerso) {
    this.userValue = user;
  }

  public getUser(): Observable<InfoPerso> {
    if (!this.userValue) {
      return this._userService.getInfoPerso().pipe(tap((user) => this.setUser(user)));
    }
    return of(this.userValue);
  }

  public getMultiple() {
    if (!this.multiple && this.multiple !== false) this.getAccessData();
    return this.multiple;
  }

  private setContactFullName(firstName: string, lastName: string) {
    this.contactFullName = firstName + ' ' + lastName;
  }

  public setCompany(company: CompanyDetails) {
    this.selectedCompany = company;
    if (company && company.countryCode)
      this.selectedCompany.country = COUNTRIES.filter((country: any) => country.alpha2Code.toLowerCase() === company.countryCode.toLowerCase())[0].name;
  }

  public getCompanyDisplayedSections(): CompanyDisplayedSections {
    if (this.selectedCompany) {
      return {
        activateCA:
          !this.selectedCompany.activite ||
          (!this.selectedCompany.activite.label.includes('santé') &&
            !this.selectedCompany.activite.label.includes('Administration') &&
            !this.selectedCompany.activite.label.includes('bancaire')),
        activateSEPA:
          this.selectedCompany.siren &&
          (this.selectedCompany.countryCode?.toUpperCase() === 'FR' ||
            this.selectedCompany.countryCode?.toUpperCase() === 'FR-TOM' ||
            this.selectedCompany.countryCode?.toUpperCase() === 'FR-DOM'),
        activateLogo: this.selectedCompany.typeCompte === 'GS1 Partenaires' && this.selectedCompany.packId !== 'DD6B09B2-4124-4D6C-822B-F3102BC49835',
        activatePacks: this.selectedCompany.typeCompte === 'GS1 Partenaires',
        hasCustomerNumber: this.selectedCompany.customerNumber !== null && this.selectedCompany.customerNumber?.trim()?.length > 0
      };
    }
  }

  public refreshAndGetCompany(): Observable<CompanyDetails> {
    this.selectedCompany = null;
    return this._compteEntrepriseService.getCompanyDetails().pipe(tap((company) => this.setCompany(company)));
  }

  public setPack(pack: string) {
    this.packValue = pack;
    this.pack.next(this.packValue);
  }

  public getAccessData(): AccessData {
    if (!this.accessDataValue) {
      const accessDataFromToken = this.getAccessDataFromToken();
      const accessData = new AccessDataByEmailInputModel();
      accessData.account = new AccountModel();
      accessData.account.companyId = accessDataFromToken.companyId ?? '';
      accessData.account.name = accessDataFromToken.companyName;
      if (accessDataFromToken.privileges instanceof Array) accessData.account.privileges = accessDataFromToken.privileges.map((x) => new PrivilegeModel(x));
      else accessData.account.privileges.push(new PrivilegeModel(accessDataFromToken.privileges));
    }
    return this.accessDataValue;
  }

  public getAccessDataFromToken(): AccessDataFromToken {
    if (this._authService.hasValidAccessToken()) {
      const token: string = this._authService.getAccessToken();
      const jwtHelper: JwtHelperService = new JwtHelperService();
      const decodedToken: DecodedAccessToken = jwtHelper.decodeToken(token);
      this.multiple = Number(decodedToken['companies_nb']) > 1;
      const accessDataFromToken = new AccessDataFromToken(decodedToken);
      this.setAccountInfo(accessDataFromToken);
      return accessDataFromToken;
    }
  }

  public getUnpaidInvoices(forceReload = false): Observable<{ count: number; toPay: Invoice[]; autoPay: Invoice[] }> {
    if (this.loadingUnpaid) {
      return this.unpaidInvoices.asObservable();
    }
    if (!this.unpaidInvoicesValue || forceReload) {
      this.loadingUnpaid = true;
      return forkJoin([this._invoiceService.getDetailInvoices('invoiceunpaid', 0, 2000), this.getMandat()]).pipe(
        tap(([invoices]) => {
          invoices.data.map((invoice) => {
            invoice.type = invoice.type ? this._translate.instant('Invoice.Types.' + invoice.type) : '';
            return invoice;
          });
          this.setUnpaidInvoices(
            invoices.count,
            invoices.data.filter((invoice) => invoice.inDebit !== true),
            invoices.data.filter((invoice) => invoice.inDebit === true)
          );
          this.loadingUnpaid = false;
        }),
        map(() => this.unpaidInvoicesValue),
        catchError((err) => {
          if (!(err && err.status && err.status === 404)) {
            this._notifService.addNotification("Vos factures impayées n'ont pu être récupérées", 'error');
          } else this.setUnpaidInvoices(0, [], []);
          this.loadingUnpaid = false;
          return of(this.unpaidInvoicesValue);
        })
      );
    }
    return new BehaviorSubject(this.unpaidInvoicesValue).asObservable();
  }

  public setUnpaidInvoices(count: number, toPay: Invoice[], autoPay: Invoice[]) {
    this.unpaidInvoicesValue = { count, toPay, autoPay };
    this.unpaidInvoices.next(this.unpaidInvoicesValue);
  }

  public removeUnpaidInvoices() {
    this.unpaidInvoicesValue = null;
    this.unpaidInvoices.next(this.unpaidInvoicesValue);
  }

  public getMandat(): Observable<AllSEPAMandates> {
    if (this.loadingMandat) return this.mandat.asObservable();
    if (!this.mandatValue) {
      this.loadingMandat = true;
      return this._mandate.getMandates().pipe(
        tap((mandates) => {
          this.setMandate(mandates);
          this.loadingMandat = false;
        }),
        catchError((err: any) => {
          if (!(err && err.status && err.status === 404)) this._notifService.addNotification("Vos mandats de prélèvement n'ont pu être récupérés", 'error');
          else this.setMandate({ Mandates: [], CurrentMandate: null });
          this.loadingMandat = false;
          return of(this.mandatValue);
        })
      );
    }
    return new BehaviorSubject(this.mandatValue).asObservable();
  }

  public setMandate(mandates: AllSEPAMandates) {
    this.mandatValue = mandates;
    this.mandat.next(this.mandatValue);
  }

  public remove(item: string) {
    localStorage.removeItem(item);
  }

  public clearStorage() {
    this.itemStorageList.forEach((item) => localStorage.removeItem(item));
    this.resetStorage();
    sessionStorage.clear();
    localStorage.clear();
  }

  public isUserAdmin() {
    return this.userValue && this.userValue.roles && this.userValue.roles.includes('administrateur');
  }

  public isUserFederation(): Observable<boolean> {
    return this.getUser().pipe(
      take(1),
      map((user) => user && user.roles && user.roles.length == 1 && user.roles.includes('Fédération'))
    );
  }
}
